import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import BigNumber from 'bignumber.js';

import {
  Cryptocurrency,
  FiatCurrency,
  SupportedCurrency,
} from '#src/__generated__/globalTypes.ts';
import { BigToBigInt } from '#src/bigint.ts';

import type {
  MonetaryAmountFragment_monetaryAmount,
  RatesFragment_rates,
} from './__generated__/monetaryAmount.graphql.ts';
import { fromLamport } from './lamport.ts';
import { fromWei } from './wei.ts';

type CurrencyWithRate = FiatCurrency | typeof SupportedCurrency.LAMPORT;

export interface MonetaryAmountParams {
  referenceCurrency: SupportedCurrency;
  eurCents?: number | string | null;
  usdCents?: number | string | null;
  gbpCents?: number | string | null;
  wei?: bigint | string | null;
  lamport?: bigint | string | null;
}

const ETH_IN_WEI = new BigNumber('1000000000000000000');
const ETH_PRECISION = 4;

const SOL_IN_LAMPORT = new BigNumber('1000000000');
const SOL_PRECISION = 3;

type MonetaryAmountCurrency =
  | 'eurCents'
  | 'usdCents'
  | 'gbpCents'
  | 'wei'
  | 'lamport';

type MonetaryAmountFiatCurrency = 'eurCents' | 'usdCents' | 'gbpCents';

const getFiatMonetaryAmountIndex = (
  currency: FiatCurrency
): MonetaryAmountFiatCurrency => {
  return `${currency.toLowerCase()}Cents` as MonetaryAmountFiatCurrency;
};

export const getMonetaryAmountDisplayCurrency = (
  monetaryAmount: Pick<
    MonetaryAmountParams,
    'eurCents' | 'usdCents' | 'gbpCents'
  >,
  userFiatCurrency: FiatCurrency
): FiatCurrency => {
  const key = getFiatMonetaryAmountIndex(userFiatCurrency);
  return monetaryAmount[key] != null ? userFiatCurrency : FiatCurrency.USD;
};

const getCurrencyWithRateMonetaryAmountIndex = (currency: CurrencyWithRate) => {
  switch (currency) {
    case SupportedCurrency.LAMPORT:
      return 'lamport';
    default:
      return getFiatMonetaryAmountIndex(currency);
  }
};

export const getMonetaryAmountIndex = (
  currency: SupportedCurrency
): MonetaryAmountCurrency => {
  switch (currency) {
    case SupportedCurrency.WEI:
      return 'wei';
    default:
      return getCurrencyWithRateMonetaryAmountIndex(currency);
  }
};

export const getMinorRate = (
  rates: RatesFragment_rates,
  currency: CurrencyWithRate
) => {
  const key = getCurrencyWithRateMonetaryAmountIndex(currency);
  return new BigNumber(rates[key].toString());
};

const rate = (
  referenceCurrency: SupportedCurrency,
  currency: SupportedCurrency,
  rates: RatesFragment_rates
) => {
  if (referenceCurrency === SupportedCurrency.WEI) {
    return getMinorRate(rates, currency as CurrencyWithRate);
  }
  if (currency === SupportedCurrency.WEI) {
    return new BigNumber(1).dividedBy(getMinorRate(rates, referenceCurrency));
  }

  return getMinorRate(rates, currency).dividedBy(
    getMinorRate(rates, referenceCurrency)
  );
};

const convertToLamport = (
  big: BigNumber,
  referenceCurrency: SupportedCurrency,
  rates: RatesFragment_rates
) =>
  big
    .multipliedBy(rate(referenceCurrency, SupportedCurrency.LAMPORT, rates))
    .dividedBy(SOL_IN_LAMPORT)
    .decimalPlaces(SOL_PRECISION, BigNumber.ROUND_HALF_UP)
    .multipliedBy(SOL_IN_LAMPORT);

const convert = (
  referenceCurrency: SupportedCurrency,
  referenceAmount: bigint | number | string,
  currency: SupportedCurrency,
  rates: RatesFragment_rates
): BigNumber => {
  if (referenceCurrency === currency)
    return new BigNumber(referenceAmount.toString());

  if (referenceCurrency === SupportedCurrency.WEI) {
    if (currency === SupportedCurrency.LAMPORT) {
      return convertToLamport(
        new BigNumber(referenceAmount.toString()).dividedBy(ETH_IN_WEI),
        referenceCurrency,
        rates
      );
    }
    return new BigNumber(referenceAmount.toString())
      .dividedBy(ETH_IN_WEI)
      .multipliedBy(rate(referenceCurrency, currency, rates))
      .decimalPlaces(0, BigNumber.ROUND_HALF_UP);
  }

  if (currency === SupportedCurrency.WEI) {
    return new BigNumber(referenceAmount.toString())
      .multipliedBy(rate(referenceCurrency, currency, rates))
      .decimalPlaces(ETH_PRECISION, BigNumber.ROUND_HALF_UP)
      .multipliedBy(ETH_IN_WEI);
  }

  if (currency === SupportedCurrency.LAMPORT) {
    return convertToLamport(
      new BigNumber(referenceAmount.toString()),
      referenceCurrency,
      rates
    );
  }

  return new BigNumber(referenceAmount.toString())
    .multipliedBy(rate(referenceCurrency, currency, rates))
    .decimalPlaces(0, BigNumber.ROUND_HALF_UP);
};

const bigValueAbove = (
  referenceCurrency: SupportedCurrency,
  referenceValue: bigint | number | string,
  currency: SupportedCurrency,
  rates: RatesFragment_rates
): BigNumber => {
  let bigValue = convert(referenceCurrency, referenceValue, currency, rates);
  const step = (() => {
    switch (currency) {
      case SupportedCurrency.WEI:
        return new BigNumber(ETH_IN_WEI).dividedBy(10 ** ETH_PRECISION);
      case SupportedCurrency.LAMPORT:
        return new BigNumber(SOL_IN_LAMPORT).dividedBy(10 ** SOL_PRECISION);
      default:
        return 1;
    }
  })();
  let bigValueInReferenceCurrency: BigNumber;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    bigValueInReferenceCurrency = convert(
      currency,
      bigValue.toNumber(),
      referenceCurrency,
      rates
    );

    if (
      bigValueInReferenceCurrency.isNaN() ||
      bigValueInReferenceCurrency.isZero()
    ) {
      return new BigNumber(0);
    }
    if (
      bigValueInReferenceCurrency.isGreaterThanOrEqualTo(
        referenceValue!.toString()
      )
    ) {
      return bigValue;
    }

    bigValue = bigValue.plus(step);
  }
};

const inCurrency = <T extends SupportedCurrency>(
  referenceCurrency: SupportedCurrency,
  referenceValue: bigint | number | string,
  currency: T,
  rates: RatesFragment_rates
): T extends typeof SupportedCurrency.WEI | typeof SupportedCurrency.LAMPORT
  ? bigint
  : number => {
  if (referenceCurrency === currency) {
    if (
      currency === SupportedCurrency.WEI ||
      currency === SupportedCurrency.LAMPORT
    ) {
      return BigInt(referenceValue) as T extends
        | typeof SupportedCurrency.WEI
        | typeof SupportedCurrency.LAMPORT
        ? bigint
        : number;
    }
    return Number(referenceValue) as T extends
      | typeof SupportedCurrency.WEI
      | typeof SupportedCurrency.LAMPORT
      ? bigint
      : number;
  }

  const value = bigValueAbove(
    referenceCurrency,
    referenceValue,
    currency,
    rates
  );

  if (
    currency === SupportedCurrency.WEI ||
    currency === SupportedCurrency.LAMPORT
  ) {
    return BigToBigInt(value) as T extends
      | typeof SupportedCurrency.WEI
      | typeof SupportedCurrency.LAMPORT
      ? bigint
      : number;
  }
  return value.toNumber() as T extends
    | typeof SupportedCurrency.WEI
    | typeof SupportedCurrency.LAMPORT
    ? bigint
    : number;
};

export class MonetaryAmount {
  public static fromRates(
    params: MonetaryAmountParams,
    rates: RatesFragment_rates
  ) {
    const { referenceCurrency } = params;
    const index = getMonetaryAmountIndex(referenceCurrency);
    if (params[index] === null || params[index] === undefined)
      throw new Error('Reference currency is required');

    const referenceValue = params[index]!;

    return new MonetaryAmount({
      referenceCurrency,
      wei: params.wei
        ? BigInt(params.wei)
        : inCurrency(
            referenceCurrency,
            referenceValue,
            SupportedCurrency.WEI,
            rates
          ),
      lamport: params.lamport
        ? BigInt(params.lamport)
        : inCurrency(
            referenceCurrency,
            referenceValue,
            SupportedCurrency.LAMPORT,
            rates
          ),

      eurCents:
        Number(params.eurCents) ||
        inCurrency(
          referenceCurrency,
          referenceValue,
          SupportedCurrency.EUR,
          rates
        ),
      usdCents:
        Number(params.usdCents) ||
        inCurrency(
          referenceCurrency,
          referenceValue,
          SupportedCurrency.USD,
          rates
        ),
      gbpCents:
        Number(params.gbpCents) ||
        inCurrency(
          referenceCurrency,
          referenceValue,
          SupportedCurrency.GBP,
          rates
        ),
    });
  }

  public static fromReferenceCurrency(
    referenceCurrency: SupportedCurrency,
    referenceValue: bigint | number | string,
    rates: RatesFragment_rates
  ) {
    return MonetaryAmount.fromRates(
      {
        referenceCurrency,
        [getMonetaryAmountIndex(referenceCurrency)]: referenceValue,
      },
      rates
    );
  }

  public static zero = new MonetaryAmount({
    referenceCurrency: SupportedCurrency.WEI,
    wei: 0n,
    eurCents: 0,
    usdCents: 0,
    gbpCents: 0,
    lamport: 0n,
  });

  referenceCurrency: SupportedCurrency;

  eurCents: number;

  usdCents: number;

  gbpCents: number;

  wei: bigint;

  lamport: bigint;

  isPositive: boolean;

  isZero: boolean;

  constructor(params: {
    referenceCurrency: SupportedCurrency;
    wei: bigint;
    eurCents: number;
    usdCents: number;
    gbpCents: number;
    lamport: bigint;
  }) {
    this.referenceCurrency = params.referenceCurrency;
    this.wei = params.wei;
    this.lamport = params.lamport;
    this.eurCents = params.eurCents;
    this.usdCents = params.usdCents;
    this.gbpCents = params.gbpCents;
    this.isPositive = this.referenceValue > 0;
    this.isZero = this.referenceValue === 0 || this.referenceValue === 0n;
  }

  get eur() {
    return this.eurCents / 100;
  }

  get usd() {
    return this.usdCents / 100;
  }

  get gbp() {
    return this.gbpCents / 100;
  }

  get eth() {
    return fromWei(this.wei);
  }

  get sol() {
    return fromLamport(this.lamport);
  }

  cryptoSubUnit(cryptocurrency: Cryptocurrency) {
    switch (cryptocurrency) {
      case Cryptocurrency.LAMPORT:
        return this.lamport;
      case Cryptocurrency.WEI:
      default:
        return this.wei;
    }
  }

  crypto(cryptocurrency: Cryptocurrency) {
    switch (cryptocurrency) {
      case Cryptocurrency.LAMPORT:
        return this.sol;
      case Cryptocurrency.WEI:
      default:
        return this.eth;
    }
  }

  get referenceValue() {
    return this[getMonetaryAmountIndex(this.referenceCurrency)];
  }

  gt(other: MonetaryAmount) {
    return this.wei > other.wei;
  }

  gte(other: MonetaryAmount) {
    return this.wei >= other.wei;
  }

  readAmount<T extends SupportedCurrency>(
    currency: T
  ): T extends FiatCurrency ? number : bigint {
    const key = getMonetaryAmountIndex(currency);

    return this[key] as T extends FiatCurrency ? number : bigint;
  }

  toMajorUnit(currency: FiatCurrency) {
    return this.readAmount(currency) / 100;
  }

  withDiscount(percentageDiscount: number, maxDiscounts: MonetaryAmount) {
    return new MonetaryAmount({
      referenceCurrency: this.referenceCurrency,
      wei: BigInt(
        BigNumber.minimum(
          new BigNumber(this.wei.toString())
            .multipliedBy(percentageDiscount)
            .decimalPlaces(0),
          new BigNumber(maxDiscounts.wei.toString())
        ).toString()
      ),
      lamport: BigInt(
        BigNumber.minimum(
          new BigNumber(this.lamport.toString())
            .multipliedBy(percentageDiscount)
            .decimalPlaces(0),
          new BigNumber(maxDiscounts.lamport.toString())
        ).toString()
      ),
      eurCents: BigNumber.minimum(
        new BigNumber(this.eurCents.toString())
          .multipliedBy(percentageDiscount)
          .decimalPlaces(0),
        new BigNumber(maxDiscounts.eurCents.toString())
      ).toNumber(),
      usdCents: BigNumber.minimum(
        new BigNumber(this.usdCents.toString())
          .multipliedBy(percentageDiscount)
          .decimalPlaces(0),
        new BigNumber(maxDiscounts.usdCents.toString())
      ).toNumber(),
      gbpCents: BigNumber.minimum(
        new BigNumber(this.gbpCents.toString())
          .multipliedBy(percentageDiscount)
          .decimalPlaces(0),
        new BigNumber(maxDiscounts.gbpCents.toString())
      ).toNumber(),
    });
  }

  divideBy(n: number) {
    return new MonetaryAmount({
      referenceCurrency: this.referenceCurrency,
      wei: BigInt(
        new BigNumber(this.wei.toString())
          .dividedBy(n)
          .decimalPlaces(0)
          .toString()
      ),
      lamport: BigInt(
        new BigNumber(this.lamport.toString())
          .dividedBy(n)
          .decimalPlaces(0)
          .toString()
      ),
      eurCents: new BigNumber(this.eurCents.toString())
        .dividedBy(n)
        .decimalPlaces(0)
        .toNumber(),
      usdCents: new BigNumber(this.eurCents.toString())
        .dividedBy(n)
        .decimalPlaces(0)
        .toNumber(),
      gbpCents: new BigNumber(this.eurCents.toString())
        .dividedBy(n)
        .decimalPlaces(0)
        .toNumber(),
    });
  }

  isEqualTo(other: MonetaryAmount) {
    return (
      this.wei === other.wei &&
      this.lamport === other.lamport &&
      this.eurCents === other.eurCents &&
      this.usdCents === other.usdCents &&
      this.gbpCents === other.gbpCents
    );
  }

  multipiedBy(factor: number) {
    return new MonetaryAmount({
      referenceCurrency: this.referenceCurrency,
      wei: this.wei * BigInt(factor),
      lamport: this.lamport * BigInt(factor),
      eurCents: this.eurCents * factor,
      usdCents: this.usdCents * factor,
      gbpCents: this.gbpCents * factor,
    });
  }

  calculateFees = (
    feesPercentagePoints: bigint
  ): {
    feesMonetaryAmount: MonetaryAmount;
    youReceiveMonetaryAmount: MonetaryAmount;
  } => {
    const feesMonetaryAmount =
      feesPercentagePoints === 0n
        ? MonetaryAmount.zero
        : new MonetaryAmount({
            eurCents:
              Number(BigInt(this.eurCents) * feesPercentagePoints) / 100,
            usdCents:
              Number(BigInt(this.usdCents) * feesPercentagePoints) / 100,
            gbpCents:
              Number(BigInt(this.gbpCents) * feesPercentagePoints) / 100,
            wei: (this.wei * feesPercentagePoints) / 100n,
            lamport: (this.lamport * feesPercentagePoints) / 100n,
            referenceCurrency: this.referenceCurrency,
          });
    const youReceiveMonetaryAmount = new MonetaryAmount({
      eurCents: this.eurCents - feesMonetaryAmount.eurCents,
      usdCents: this.usdCents - feesMonetaryAmount.usdCents,
      gbpCents: this.gbpCents - feesMonetaryAmount.gbpCents,
      wei: this.wei - feesMonetaryAmount.wei,
      lamport: this.lamport - feesMonetaryAmount.lamport,
      referenceCurrency: this.referenceCurrency,
    });

    return {
      feesMonetaryAmount,
      youReceiveMonetaryAmount,
    };
  };

  // Divides by 10000 so sub-percent rates (325, 250, 425) stay exact, unlike
  // calculateFees which takes whole percentage points and would truncate them.
  calculateFeesFromBasisPoints = (feeBasisPoints: number): MonetaryAmount => {
    if (!feeBasisPoints) return MonetaryAmount.zero;
    const basisPoints = BigInt(feeBasisPoints);
    return new MonetaryAmount({
      referenceCurrency: this.referenceCurrency,
      eurCents: Number(BigInt(this.eurCents) * basisPoints) / 10000,
      usdCents: Number(BigInt(this.usdCents) * basisPoints) / 10000,
      gbpCents: Number(BigInt(this.gbpCents) * basisPoints) / 10000,
      wei: (this.wei * basisPoints) / 10000n,
      lamport: (this.lamport * basisPoints) / 10000n,
    });
  };
}

export const monetaryAmountFragment = gql`
  fragment MonetaryAmountFragment_monetaryAmount on MonetaryAmount {
    eurCents
    usdCents
    gbpCents
    wei
    referenceCurrency
    lamport
  }
` as TypedDocumentNode<MonetaryAmountFragment_monetaryAmount>;

export const ratesFragment = gql`
  fragment RatesFragment_rates on Rates {
    eurCents
    usdCents
    gbpCents
    lamport
  }
` as TypedDocumentNode<RatesFragment_rates>;
